import Icon from 'components/atoms/Icon';
import {
  ProjectAction,
  ProjectJobRoleFragment,
  ProjectStatus,
} from 'generated/graphql';
import { TCognitiveSkillsVersion } from 'helpers/cognitiveSkills';
import {
  TMissingCategorySkillsInRoles,
  TSkillsByCategory,
} from 'helpers/softSkills';
import { useProjectActionsValidation } from 'hooks/useProjectPolicyActions/useProjectPolicyActions';
import { FC, useMemo, useState } from 'react';
import SkillsCategoryOverview from 'views/Project/components/SkillsCategoryOverview/SkillsCategoryOverview';
import BenchmarkGroup from '../BenchmarkGroup/BenchmarkGroup';
import { DuplicateJobRoleModal } from '../DuplicateJobRoleModal/DuplicateJobRoleModal';
import ProjectJobRoleRow from '../JobRoleRow/ProjectJobRoleRow';
import { TSkillOverview } from '../ProjectJobRolesOverviewSkillsList/ProjectJobRolesOverviewSkillsList.types';
import styles from './ProjectJobRolesOverviewPresentational.module.scss';
import {
  Button,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import { AddProjectJobRoleDropdownButton } from './ProjectJobRolesButtons';

interface IProjectJobRolesOverviewPresentational {
  softSkills: TSkillsByCategory;
  technicalSkills: TSkillOverview[];
  projectJobRoles: ProjectJobRoleFragment[];
  projectId: number;
  companyId: number;
  hasSkills: boolean;
  cognitiveSkillsVersion: TCognitiveSkillsVersion;
  missingCategorySkillsInRoles: TMissingCategorySkillsInRoles;
  projectStatus: ProjectStatus;
  hasAssessmentStage: boolean;
}

const ProjectJobRolesOverviewPresentational: FC<IProjectJobRolesOverviewPresentational> =
  ({
    softSkills,
    technicalSkills,
    projectId,
    companyId,
    projectJobRoles,
    hasSkills,
    missingCategorySkillsInRoles,
    projectStatus,
    hasAssessmentStage,
  }) => {
    const { projectPolicyActions } = useProjectActionsValidation({
      projectId,
    });

    const isMultiMatchProject = projectJobRoles.length > 1;
    const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(
      () => new Set(),
    );
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

    const skillsByRoleId = useMemo(() => {
      const map = new Map<
        number,
        { soft: Set<number>; technical: Set<number> }
      >();
      for (const projectJobRole of projectJobRoles) {
        const skillsProfile = projectJobRole.jobRole.skillsProfiles.find(
          (sp) => sp.id === projectJobRole.skillsProfileId,
        );
        const soft = new Set<number>();
        const technical = new Set<number>();
        skillsProfile?.successProfileSoftSkills?.forEach((s) =>
          soft.add(s.softSkillId),
        );
        skillsProfile?.successProfileTechnicalSkills?.forEach((s) => {
          if (s.technicalSkill) technical.add(s.technicalSkill.id);
        });
        map.set(projectJobRole.id, { soft, technical });
      }
      return map;
    }, [projectJobRoles]);

    const highlightedSoftSkillIds = useMemo(() => {
      if (selectedRoleIds.size === 0) return undefined;
      const union = new Set<number>();
      selectedRoleIds.forEach((roleId) => {
        skillsByRoleId.get(roleId)?.soft.forEach((id) => union.add(id));
      });
      return union;
    }, [selectedRoleIds, skillsByRoleId]);

    const highlightedTechnicalSkillIds = useMemo(() => {
      if (selectedRoleIds.size === 0) return undefined;
      const union = new Set<number>();
      selectedRoleIds.forEach((roleId) => {
        skillsByRoleId.get(roleId)?.technical.forEach((id) => union.add(id));
      });
      return union;
    }, [selectedRoleIds, skillsByRoleId]);

    return (
      <>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              {projectJobRoles.length > 1
                ? 'Role Skills Profiles'
                : 'Role Skills Profile'}
            </div>
            <div className={styles.subheaderActions}>
              <AddProjectJobRoleDropdownButton
                projectId={projectId}
                projectStatus={projectStatus}
                isEnabledProjectAction={
                  projectPolicyActions[ProjectAction.CreateProjectJobRole]
                }
                onDuplicateExistingRole={() => setIsDuplicateModalOpen(true)}
              />
            </div>
          </div>
          <BenchmarkGroup
            projectJobRoles={projectJobRoles}
            hasAssessmentStage={hasAssessmentStage}
          />
          <div className={styles.jobRolesList} data-testid="job-roles-list">
            {projectJobRoles.map((projectJobRole) => (
              <ProjectJobRoleRow
                key={projectJobRole.id}
                projectJobRole={projectJobRole}
                projectId={projectId}
                isEnabledProjectAction={
                  projectPolicyActions[ProjectAction.RemoveProjectJobRole]
                }
                projectStatus={projectStatus}
                isMultiMatchProject={isMultiMatchProject}
              />
            ))}
          </div>

          <div className={styles.header}>
            {isMultiMatchProject && (
              <div className={styles.subheaderTitle}>Skills to assess</div>
            )}
          </div>

          {isMultiMatchProject && hasSkills && (
            <div
              className={styles.roleFilterChips}
              data-testid="role-filter-chips"
            >
              <span className={styles.quickFilterLabel}>Quick filter:</span>
              {projectJobRoles.map((projectJobRole) => {
                const isSelected = selectedRoleIds.has(projectJobRole.id);
                return (
                  <Tag
                    key={projectJobRole.id}
                    tagColor={
                      isSelected ? TagColor.INTERACTIVE : TagColor.DISABLED
                    }
                    size={TagSize.SMALL}
                    isDismissible={false}
                    ariaLabel={projectJobRole.jobRole.name}
                    onClick={() =>
                      setSelectedRoleIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(projectJobRole.id)) {
                          next.delete(projectJobRole.id);
                        } else {
                          next.add(projectJobRole.id);
                        }
                        return next;
                      })
                    }
                  >
                    {projectJobRole.jobRole.name}
                  </Tag>
                );
              })}
              {selectedRoleIds.size > 0 && (
                <Button
                  variant="text"
                  size="small"
                  leftIcon={<Icon icon="ic_close_gray" />}
                  onClick={() => setSelectedRoleIds(new Set())}
                  ariaLabel="Clear role filter"
                  data-testid="role-filter-clear-button"
                  className={styles.clearFilterButton}
                >
                  Clear filter
                </Button>
              )}
            </div>
          )}

          {!hasSkills && (
            <div className={styles.containerEmpty}>
              <Icon icon="empty" />
              <p className={styles.containerEmptyText}>
                This role doesn't have a Role Skills Profile yet.
              </p>
            </div>
          )}

          {hasSkills && (
            <SkillsCategoryOverview
              softSkills={softSkills}
              technicalSkills={technicalSkills}
              missingCategorySkillsInRoles={missingCategorySkillsInRoles}
              highlightedSoftSkillIds={highlightedSoftSkillIds}
              highlightedTechnicalSkillIds={highlightedTechnicalSkillIds}
            />
          )}
        </div>
        {isDuplicateModalOpen && (
          <DuplicateJobRoleModal
            projectId={projectId}
            companyId={companyId}
            onClose={() => setIsDuplicateModalOpen(false)}
          />
        )}
      </>
    );
  };

export default ProjectJobRolesOverviewPresentational;
